// src/pages/Home/Home.tsx
import React from 'react'
import { Badge, Button, Dropdown } from 'react-bootstrap'
import { AuthContext, getAdminRecord, saveLog, dbRead } from '../../_services/firebase'
import { DB_ADMIN, DB_FOLLOWER } from '../../_config/db_paths'
import LoadingPage from '../../components/LoadingPage/LoadingPage'
import ModuleModal from '../../components/ModuleModal/ModuleModal'
import type { AdminRecord, UserRole } from '../../_types'

import RegisterForm  from '../modules/DataEntry/RegisterForm/RegisterForm'
import EditCustomer  from '../modules/DataEntry/EditCustomer/EditCustomer'
import ApproveCredit from '../modules/DataEntry/ApproveCredit/ApproveCredit'
import PayoutManager from '../modules/Accounting/PayoutManager/PayoutManager'
import DebtManager   from '../modules/Accounting/DebtManager/DebtManager'
import DebtTracking  from '../modules/DebtTracking/DebtTracking'
import Archive       from '../modules/Archive/Archive'
import RoleManager   from '../modules/Admin/RoleManager/RoleManager'
import FormBuilder   from '../modules/Admin/FormBuilder/FormBuilder'
import LogViewer     from '../modules/Admin/LogViewer/LogViewer'
import Dashboard      from '../modules/Dashboard/Dashboard'
import PhotoImporter  from '../modules/Admin/PhotoImporter/PhotoImporter'
import BackupManager  from '../modules/Admin/BackupManager/BackupManager'

interface ModuleDef {
  key: string
  icon: string
  label: string
  group: string
  roles: UserRole[]
  badge?: string
  element: React.ReactNode
  needRole?: boolean
}

interface GroupDef { key: string; label: string }

const GROUPS: GroupDef[] = [
  { key: 'overview', label: 'ภาพรวม' },
  { key: 'register', label: 'สมัครลูกค้าใหม่' },
  { key: 'account',  label: 'จัดการบัญชี' },
  { key: 'tracking', label: 'จัดการพนักงานเร่งรัดหนี้สิน' },
  { key: 'data',     label: 'ข้อมูลทั้งหมด' },
  { key: 'admin',    label: 'ตั้งค่าการพนักงาน' },
]

const ALL_MODULES: ModuleDef[] = [
  { key: 'Dashboard',          icon: 'fa-solid fa-chart-pie',           label: 'สรุปผลภาพรวม',      group: 'overview', roles: ['super admin', 'ceohead', 'acchead', 'debhead'],    element: <Dashboard /> },
  { key: 'FormRegister',       icon: 'fa-brands fa-wpforms',            label: 'ฟอร์มลงทะเบียน',     group: 'register', roles: ['super admin', 'makhead', 'makadmn', 'marketg','acchead',  'account', 'debhead'],    badge: 'ล่าสุด', element: <RegisterForm /> },
  { key: 'EditForm',           icon: 'fa-solid fa-table-list',          label: 'แก้ไขฟอร์มลูกค้า',     group: 'register', roles: ['super admin', 'makhead', 'makadmn', 'marketg','acchead',  'account', 'debhead'],                                    element: <EditCustomer /> },
  { key: 'ApproveCredit',      icon: 'fa-solid fa-file-signature',      label: 'ฟอร์มอนุมัติสินเชื่อ',    group: 'register', roles: ['super admin', 'makhead', 'ceohead', 'acchead'],                element: <ApproveCredit /> },
  { key: 'Payout',             icon: 'fa-solid fa-hand-holding-dollar', label: 'ลูกค้ารอรับเงิน',       group: 'account',  roles: ['super admin','ceohead',  'acchead', 'account'],                element: <PayoutManager /> },
  { key: 'DebtManager',        icon: 'fa-solid fa-receipt',             label: 'จัดการหนี้สิน',        group: 'account',  roles: ['super admin', 'acchead', 'account'],                element: <DebtManager /> },
  /*{ key: 'DebtTrackerManager', icon: 'fa-solid fa-person-biking',       label: 'จัดการผู้ติดตามหนี้สิน',  group: 'account',  roles: ['super admin','ceohead',  'acchead', 'debhead', 'account'],                element: null, needRole: true },*/
  /*{ key: 'DebtDetail',         icon: 'fa-solid fa-user-group',          label: 'รายละเอียดลูกค้า',     group: 'tracking', roles: ['super admin','ceohead', 'debhead', 'debadmn'],     element: null, needRole: true },*/
  { key: 'DebtFollow',         icon: 'fa-solid fa-id-card',             label: 'ติดตามลูกค้า',        group: 'tracking', roles: ['super admin', 'debhead', 'debadmn', 'debfllw'],     element: null, needRole: true },
  { key: 'Archive',            icon: 'fa-solid fa-box-archive',         label: 'แก้ไขข้อมูลลูกค้า',     group: 'data',     roles: ['super admin', 'ceohead', 'acchead', 'account'],      element: <Archive /> },
  { key: 'RoleManager',        icon: 'fa-solid fa-user-shield',         label: 'จัดการพนักงาน',       group: 'admin',    roles: ['super admin','ceohead'],                                    element: <RoleManager /> },
  { key: 'FormBuilder',        icon: 'fa-solid fa-sliders',             label: 'ตั้งค่าแบบฟอร์ม',      group: 'admin',    roles: ['super admin','ceohead', 'makhead', 'makadmn', 'marketg', 'acchead', 'debhead'],                                    element: <FormBuilder /> },
  { key: 'LogViewer',          icon: 'fa-solid fa-clock-rotate-left',   label: 'ประวัติการใช้งาน',     group: 'admin',    roles: ['super admin','ceohead'],                                    element: <LogViewer /> },
  { key: 'PhotoImporter',      icon: 'fa-solid fa-file-import',         label: 'นำเข้ารูปภาพ (JSON)', group: 'admin',    roles: ['super admin'],                                    element: <PhotoImporter /> },
  { key: 'BackupManager',      icon: 'fa-solid fa-database',            label: 'จัดการฐานข้อมูล',      group: 'admin',    roles: ['super admin'],                                    element: <BackupManager /> },
]

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'super admin', label: 'ผู้ดูแลระบบ' },
  { value: 'ceohead',     label: 'หุ้นส่วน' },
  { value: 'makhead',     label: 'หัวหน้าตลาด' },
  { value: 'marketg',     label: 'พนักงานตลาด' },
  { value: 'acchead',     label: 'หัวหน้าบัญชี' },
  { value: 'account',     label: 'พนักงานบัญชี' },
  { value: 'debhead',     label: 'หัวหน้าเร่งรัด' },
  { value: 'debadmn',     label: 'ผู้ดูแลเร่งรัด' },
  { value: 'debfllw',     label: 'พนักงานติดตาม' },
]

// ─── Module Card ──────────────────────────────────────────────
function ModuleCard({ mod, active, onClick }: {
  mod: ModuleDef; active: boolean; onClick: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        border: active ? '2px solid #ffc107' : '1.5px solid #e0e0e0',
        borderRadius: 10,
        padding: '18px 12px',
        cursor: 'pointer',
        backgroundColor: active ? '#fffbf0' : '#fff',
        textAlign: 'center',
        transition: 'all 0.15s',
        minWidth: 130,
        flex: '1 1 130px',
        maxWidth: 200,
        position: 'relative',
      }}
    >
      {mod.badge && (
        <Badge bg='warning' text='dark' style={{ position: 'absolute', top: 8, right: 8, fontSize: 10 }}>
          {mod.badge}
        </Badge>
      )}
      <i className={mod.icon} style={{ fontSize: 26, color: '#555', marginBottom: 8, display: 'block' }} />
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{mod.label}</div>
      <div style={{ fontSize: 11, color: '#aaa' }}>เปิด</div>
    </div>
  )
}

// ─── Home ─────────────────────────────────────────────────────
export default function Home({ uid }: { uid: string }) {
  const { signOut } = React.useContext(AuthContext)

  const [admin,       setAdmin]       = React.useState<AdminRecord | null>(null)
  const [loading,     setLoading]     = React.useState(true)
  const [activeKey,   setActiveKey]   = React.useState<string | null>(null)
  const [previewRole,   setPreviewRole]   = React.useState<UserRole | null>(null)
  const [previewEmpUid, setPreviewEmpUid] = React.useState<string | null>(null)
  const [followerEmps,  setFollowerEmps]  = React.useState<{ uid: string; name: string; email: string; zones: string[] }[]>([])

  React.useEffect(() => {
    ;(async () => {
      try {
        const rec = await getAdminRecord(uid)
        if (rec) { setAdmin(rec); await saveLog(uid, 'login', uid) }
      } finally { setLoading(false) }
    })()
  }, [uid])

  // Load employees ที่เป็น debfllw เมื่อ preview role นั้น
  React.useEffect(() => {
    if (!['debfllw', 'debadmn', 'debhead'].includes(previewRole || '')) {
      setFollowerEmps([])
      setPreviewEmpUid(null)
      return
    }
    ;(async () => {
      try {
        const [adminRaw, followerRaw] = await Promise.all([
          dbRead<Record<string, any>>(DB_ADMIN),
          dbRead<Record<string, any>>(DB_FOLLOWER),
        ])
        const emps = Object.values(adminRaw || {})
          .filter((e: any) => e.role === previewRole)
          .map((e: any) => ({
            uid:   e.key || e.uid || '',
            name:  e.name || e.email || '',
            email: e.email || '',
            zones: followerRaw?.[e.key || e.uid]?.zones || [],
          }))
        setFollowerEmps(emps)
        setPreviewEmpUid(emps[0]?.uid || null)
      } catch {}
    })()
  }, [previewRole])

  const realRole    = (admin?.role ?? '') as UserRole
  const isSuperAdmin = realRole === 'super admin'

  // ใช้ previewRole ถ้ากำลัง preview อยู่ (super admin เท่านั้น)
  const role = (isSuperAdmin && previewRole) ? previewRole : realRole
  const isPreviewMode = isSuperAdmin && previewRole !== null

const modules = React.useMemo(() => {
  const withRole = ALL_MODULES.map((m) =>
    m.needRole
      ? {
          ...m,
          element: (
            <DebtTracking
              role={role}
              previewUid={
                isPreviewMode && previewEmpUid
                  ? previewEmpUid
                  : undefined
              }
            />
          ),
        }
      : m
  );

  return withRole.filter((m) => m.roles.includes(role));
}, [role, isPreviewMode, previewEmpUid]);

  const groups = React.useMemo(
    () => GROUPS.filter((g) => modules.some((m) => m.group === g.key)),
    [modules]
  )

  // ─── Early returns ────────────────────────────────────────
  if (loading) return <LoadingPage text='กำลังตรวจสอบสิทธิ์...' />

  if (!admin) {
    return (
      <div className='d-flex flex-column justify-content-center align-items-center' style={{ minHeight: '100vh', gap: 12 }}>
        <i className='fa-solid fa-ban' style={{ fontSize: 40, color: '#dc3545' }} />
        <div style={{ fontWeight: 700, fontSize: 16 }}>ไม่มีสิทธิ์เข้าถึงระบบ</div>
        <div className='text-muted' style={{ fontSize: 13 }}>กรุณาติดต่อผู้ดูแลระบบ</div>
        <Button variant='outline-secondary' size='sm' onClick={signOut}>ออกจากระบบ</Button>
      </div>
    )
  }

  const roleLabel = ROLE_OPTIONS.find((r) => r.value === role)?.label || role

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>

      {/* ─── Preview Mode Banner ─────────────────────────────── */}
      {isPreviewMode && (
        <div style={{
          backgroundColor: '#ff6f00',
          color: '#fff',
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 13,
          fontWeight: 600,
        }}>
          <div>
            <i className='fa-solid fa-eye me-2' />
            กำลังดูในมุมมอง: <strong>{roleLabel}</strong>
            &nbsp;— โมดูลที่แสดงอยู่นี้คือสิ่งที่ {roleLabel} จะเห็น
          </div>
          <div className='d-flex align-items-center gap-2'>
            {/* Employee selector — เฉพาะ debfllw/debadmn/debhead */}
            {followerEmps.length > 0 && (
              <div className='d-flex align-items-center gap-2'>
                <span>
                  uid ของพนักงานที่เลือก: <strong>{previewEmpUid}</strong>
                </span>
                <span style={{ fontSize: 12, opacity: 0.85 }}>
                  <i className='fa-solid fa-user me-1' />ดูในฐานะ:
                </span>
                <select
                  value={previewEmpUid || ''}
                  onChange={(e) => setPreviewEmpUid(e.target.value || null)}
                  style={{
                    fontSize: 12,
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: 'none',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    cursor: 'pointer',
                    outline: 'none',
                    maxWidth: 200,
                  }}
                >
                  {followerEmps.map((emp) => (
                    <option key={emp.uid} value={emp.uid} style={{ color: '#333', backgroundColor: '#fff' }}>
                      {emp.name} {emp.zones.length > 0 ? `(${emp.zones.join(',')})` : '(ยังไม่มีโซน)'}
                    </option>
                  ))}
                </select>
                {/* แสดงโซนของคนที่เลือก */}
                {previewEmpUid && (() => {
                  const emp = followerEmps.find((e) => e.uid === previewEmpUid)
                  return emp?.zones.length ? (
                    <span style={{ fontSize: 11, backgroundColor: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: 12 }}>
                      โซน: {emp.zones.join(', ')}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, opacity: 0.7 }}>ไม่มีโซน</span>
                  )
                })()}
              </div>
            )}
            {followerEmps.length === 0 && ['debfllw','debadmn','debhead'].includes(previewRole || '') && (
              <span style={{ fontSize: 12, opacity: 0.7 }}>
                <i className='fa-solid fa-circle-info me-1' />ไม่มีพนักงาน {ROLE_OPTIONS.find(r=>r.value===previewRole)?.label} ในระบบ
              </span>
            )}
            <Button
              size='sm'
              variant='light'
              style={{ fontSize: 12, color: '#ff6f00', fontWeight: 700 }}
              onClick={() => { setPreviewRole(null); setActiveKey(null) }}
            >
              <i className='fa-solid fa-xmark me-1' />ออกจากโหมด Preview
            </Button>
          </div>
        </div>
      )}

      {/* ─── Top Bar ─────────────────────────────────────────── */}
      <div style={{
        backgroundColor: '#fff',
        borderBottom: `2px solid ${isPreviewMode ? '#ff6f00' : '#ffc107'}`,
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: isPreviewMode ? 41 : 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36,
            backgroundColor: isPreviewMode ? '#ff6f00' : '#ffc107',
            borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className='fa-solid fa-building-columns' style={{ color: '#fff', fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>KTFC Loan System {process.env.REACT_APP_VERSION}</div>
            <div style={{ fontSize: 11, color: '#888' }}>ระบบจัดการสินเชื่อ lastest update : {process.env.REACT_APP_LAST_UPDATED}</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* ─── Preview badge ────────────────────────── */}
          {isPreviewMode ? (
            <Badge bg='warning' text='dark' style={{ fontSize: 11 }}>
              <i className='fa-solid fa-eye me-1' />Preview: {roleLabel}
            </Badge>
          ) : (
            <Badge bg='warning' text='dark' style={{ fontSize: 11 }}>
              {roleLabel}
            </Badge>
          )}

          <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>
            {admin.name || admin.email}
          </span>

          <Dropdown align='end'>
            <Dropdown.Toggle variant='outline-secondary' size='sm' style={{ fontSize: 12 }}>
              <i className='fa-solid fa-gear' />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.ItemText style={{ fontSize: 12, color: '#888' }}>
                {admin.email}
              </Dropdown.ItemText>
              <Dropdown.ItemText style={{ fontSize: 11, color: '#aaa' }}>
                Role จริง: Super Admin
              </Dropdown.ItemText>

              {/* ─── Preview role section (super admin เท่านั้น) ── */}
              {isSuperAdmin && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.ItemText style={{ fontSize: 11, fontWeight: 700, color: '#ff6f00' }}>
                    <i className='fa-solid fa-eye me-1' />ทดสอบมุมมอง Role
                  </Dropdown.ItemText>
                  {ROLE_OPTIONS.map((r) => (
                    <Dropdown.Item
                      key={r.value}
                      style={{
                        fontSize: 12,
                        backgroundColor: previewRole === r.value ? '#fff3e0' : undefined,
                        color: previewRole === r.value ? '#ff6f00' : undefined,
                        fontWeight: previewRole === r.value ? 700 : 400,
                      }}
                      onClick={() => {
                        setPreviewRole(r.value === realRole ? null : r.value)
                        setActiveKey(null)
                      }}
                    >
                      {previewRole === r.value
                        ? <><i className='fa-solid fa-check me-2' />{r.label}</>
                        : <><i className='fa-solid fa-user me-2' />{r.label}</>
                      }
                    </Dropdown.Item>
                  ))}
                  {isPreviewMode && (
                    <Dropdown.Item
                      style={{ fontSize: 12, color: '#dc3545' }}
                      onClick={() => { setPreviewRole(null); setActiveKey(null) }}
                    >
                      <i className='fa-solid fa-xmark me-2' />ออกจากโหมด Preview
                    </Dropdown.Item>
                  )}
                </>
              )}

              <Dropdown.Divider />
              <Dropdown.Item style={{ fontSize: 13 }} onClick={signOut}>
                <i className='fa-solid fa-right-from-bracket me-2' />ออกจากระบบ
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* ─── Module Grid ─────────────────────────────────────── */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
        {groups.map((group) => {
          const groupModules = modules.filter((m) => m.group === group.key)
          return (
            <div key={group.key} style={{ marginBottom: 32 }}>
              <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 15, marginBottom: 14, color: '#444' }}>
                {group.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
                {groupModules.map((mod) => (
                  <ModuleCard
                    key={mod.key}
                    mod={mod}
                    active={activeKey === mod.key}
                    onClick={() => setActiveKey(mod.key)}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* ─── Module Modals ───────────────────────────────────── */}
      {modules.map((mod) => (
        <ModuleModal
          key={mod.needRole ? `${mod.key}_${previewEmpUid || 'none'}` : mod.key}
          show={activeKey === mod.key}
          title={mod.label}
          icon={mod.icon}
          onClose={() => setActiveKey(null)}
        >
          {mod.element}
        </ModuleModal>
      ))}
    </div>
  )
}